<template>
  <div class="calendar-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('calendar.title') }}</h1>
        <p class="page-sub">{{ t('calendar.sub') }}</p>
      </div>
      <div class="cal-nav">
        <button class="btn btn-ghost btn-sm" @click="goToday">{{ t('calendar.today') }}</button>
        <button class="btn btn-icon cal-arrow" :title="t('calendar.prev')" @click="shiftMonth(-1)">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span class="cal-month">{{ monthLabel }}</span>
        <button class="btn btn-icon cal-arrow" :title="t('calendar.next')" @click="shiftMonth(1)">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>

    <div class="cal-grid cal-weekdays">
      <span v-for="w in weekdays" :key="w" class="cal-wd">{{ w }}</span>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else class="cal-grid cal-cells">
      <div
        v-for="c in cells"
        :key="c.key"
        class="cal-cell"
        :class="{ 'out-month': !c.inMonth, today: c.today }"
      >
        <span class="cal-daynum">{{ c.date.getDate() }}</span>
        <div class="cal-tasks">
          <button
            v-for="task in (tasksByDay[c.key] || [])"
            :key="task.id"
            class="cal-chip"
            :class="'u-' + task.urgency"
            :style="{ '--chip': task.project_color || 'var(--accent)' }"
            :title="task.title"
            @click="openTask(task)"
          >{{ task.title }}</button>
        </div>
      </div>
    </div>

    <TaskDetailDrawer
      v-if="drawerTask"
      :task="drawerTask"
      :projects="projects"
      @close="drawerTask = null"
      @saved="drawerTask = null; load()"
      @changed="load()"
      @complete="completeTask"
      @reopen="reopenTask"
      @restore="restoreTask"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import TaskDetailDrawer from '@/components/TaskDetailDrawer.vue'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'
import { onRealtime, debounce } from '@/composables/useRealtime'
import api from '@/api'

function ymd(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export default {
  name: 'CalendarView',
  components: { TaskDetailDrawer },
  setup() {
    const { t, locale } = useI18n()
    const toast = useToast()
    const intlLocale = computed(() => (locale.value === 'he' ? 'he-IL' : 'en-US'))

    const now    = new Date()
    const cursor = ref(new Date(now.getFullYear(), now.getMonth(), 1))
    const tasks  = ref([])
    const projects = ref([])
    const loading  = ref(true)
    const drawerTask = ref(null)

    const monthLabel = computed(() =>
      cursor.value.toLocaleDateString(intlLocale.value, { month: 'long', year: 'numeric' })
    )
    // Sun–Sat short names (Jan 1 2023 is a Sunday).
    const weekdays = computed(() => {
      const fmt = new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' })
      return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)))
    })

    // 6-week grid (42 cells) starting on the Sunday on/before the 1st.
    const cells = computed(() => {
      const y = cursor.value.getFullYear(), m = cursor.value.getMonth()
      const start = new Date(y, m, 1 - new Date(y, m, 1).getDay())
      const todayKey = ymd(new Date())
      return Array.from({ length: 42 }, (_, i) => {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
        return { date: d, key: ymd(d), inMonth: d.getMonth() === m, today: ymd(d) === todayKey }
      })
    })

    const tasksByDay = computed(() => {
      const map = {}
      for (const task of tasks.value) {
        if (!task.due_at) continue
        const k = String(task.due_at).slice(0, 10)
        ;(map[k] || (map[k] = [])).push(task)
      }
      return map
    })

    async function load() {
      loading.value = true
      const list = cells.value
      try {
        tasks.value = (await api.getTasks({ due_from: list[0].key, due_to: list[41].key })).data || []
      } catch { tasks.value = [] } finally { loading.value = false }
    }
    async function loadProjects() {
      try { projects.value = (await api.getProjects()).data || [] } catch {}
    }

    function shiftMonth(delta) {
      cursor.value = new Date(cursor.value.getFullYear(), cursor.value.getMonth() + delta, 1)
    }
    function goToday() {
      const n = new Date()
      cursor.value = new Date(n.getFullYear(), n.getMonth(), 1)
    }
    function openTask(task) { drawerTask.value = task }

    async function completeTask(task) {
      try { await api.archiveTask(task.id); drawerTask.value = null; await load(); toast.success(t('toast.taskCompleted')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    async function reopenTask(task) {
      try { await api.reopenTask(task.id); await load(); toast.success(t('toast.taskReopened')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    async function restoreTask(task) {
      try { await api.restoreTask(task.id); drawerTask.value = null; await load(); toast.success(t('toast.taskRestored')) }
      catch { toast.error(t('toast.actionFailed')) }
    }

    watch(cursor, load)
    let offRealtime = () => {}
    onMounted(() => {
      load(); loadProjects()
      offRealtime = onRealtime('task.changed', debounce(load, 400))
    })
    onUnmounted(() => offRealtime())

    return {
      t, loading, cells, weekdays, monthLabel, tasksByDay, projects, drawerTask,
      shiftMonth, goToday, openTask, load, completeTask, reopenTask, restoreTask,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.cal-nav { display: flex; align-items: center; gap: 8px; }
.cal-arrow { padding: 6px; }
.cal-month { min-width: 140px; text-align: center; font-size: var(--fs-body-lg); font-weight: 700; }

.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
.cal-weekdays { margin-bottom: 8px; }
.cal-wd {
  text-align: center; font-size: 12px; font-weight: 600;
  color: var(--muted); text-transform: uppercase; letter-spacing: .03em;
}

.cal-cells { grid-auto-rows: minmax(104px, 1fr); }
.cal-cell {
  background: var(--surface);
  border-radius: var(--radius-sm);
  padding: 8px;
  display: flex; flex-direction: column; gap: 6px;
  box-shadow: 0 1px 2px rgba(20,24,40,.05);
  min-width: 0;
}
.cal-cell.out-month { background: var(--surface-2); }
.cal-cell.out-month .cal-daynum { color: var(--faint); }
.cal-cell.today { box-shadow: inset 0 0 0 2px var(--accent); }
.cal-daynum { font-size: 13px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
.cal-cell.today .cal-daynum { color: var(--accent-ink); }

.cal-tasks { display: flex; flex-direction: column; gap: 4px; min-height: 0; overflow: hidden; }
.cal-chip {
  display: block; width: 100%; text-align: start;
  border: none; cursor: pointer;
  background: color-mix(in srgb, var(--chip) 16%, transparent);
  color: var(--text);
  border-inline-start: 3px solid var(--chip);
  border-radius: 6px; padding: 3px 7px;
  font-size: 12px; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cal-chip:hover { background: color-mix(in srgb, var(--chip) 26%, transparent); }
.cal-chip.u-critical { --chip: var(--danger); }
.cal-chip.u-high { --chip: var(--warning, #e0952b); }

@media (max-width: 640px) {
  .cal-cells { grid-auto-rows: minmax(74px, 1fr); }
  .cal-chip { font-size: 11px; padding: 2px 5px; }
}
</style>
